// App.js
import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthProvider';
import { UserPrefsProvider, useUserPrefs } from './UserPrefsProvider';
import { UIProvider } from './UIContext';
import { SessionProvider } from './SessionProvider';
import { ActiveSessionProvider } from './ActiveSessionContext';
import Navigation from './Navigation';
import SessionBanner from './SessionBanner';
import Login from './Login';
import { flushPendingWrites } from './utils/flushPendingWrites';
import { RecordingProvider } from './RecordingContext';
import RecordingBanner from './components/RecordingBanner';
import { OnboardingProvider, OnboardingGate } from './onboarding';

function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  const [navOffset, setNavOffset] = useState(52);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    const syncNavOffset = () => {
      const navEl = document.querySelector('.nav-container');
      if (!navEl) return;
      const measured = Math.round(navEl.getBoundingClientRect().height || 52);
      setNavOffset(measured);
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    window.addEventListener('resize', syncNavOffset);
    syncNavOffset();
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('resize', syncNavOffset);
    };
  }, []);

  if (!offline) return null;

  return (
    <div style={{
      position: 'fixed',
      top: `${Math.max(navOffset - 1, 0)}px`,
      left: 0,
      right: 0,
      zIndex: 999,
      background: 'linear-gradient(180deg, rgba(156, 108, 108, 0.92), rgba(140, 94, 94, 0.92))',
      borderTop: '1px solid rgba(255, 255, 255, 0.18)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.14)',
      color: '#fff',
      textAlign: 'center',
      padding: '6px 12px',
      fontSize: '0.85rem',
      fontWeight: 'bold'
    }}>
      You are offline — changes will sync when reconnected
    </div>
  );
}

function AppLayout({ prefs }) {
  const { user } = useAuth();

  useEffect(() => {
    const handleOnline = () => flushPendingWrites(user);
    window.addEventListener('online', handleOnline);
    flushPendingWrites(user);
    return () => window.removeEventListener('online', handleOnline);
  }, [user]);

  useEffect(() => {
    const isiOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent)
      || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    if (!isiOS) return undefined;

    const isEditable = (el) => {
      if (!el || !(el instanceof HTMLElement)) return false;
      if (el.isContentEditable) return true;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    };

    const handleTouchStart = (e) => {
      const active = document.activeElement;
      if (!isEditable(active)) return;
      const target = e.target;
      if (target instanceof HTMLElement && (target === active || target.closest('input, textarea, select, [contenteditable="true"]'))) return;
      if (typeof active.blur === 'function') active.blur();
    };

    document.addEventListener('touchstart', handleTouchStart, { capture: true, passive: true });
    return () => document.removeEventListener('touchstart', handleTouchStart, { capture: true });
  }, []);

  return (
    <UIProvider initialUiSize={prefs.uiSize}>
      <ActiveSessionProvider>
        <RecordingProvider>
          <Navigation />
          <OfflineBanner />
          <SessionBanner />
          <RecordingBanner />
          <Outlet />
        </RecordingProvider>
      </ActiveSessionProvider>
    </UIProvider>
  );
}

function AppLayoutWrapper() {
  const { prefs } = useUserPrefs();
  if (!prefs) return <div>Loading preferences...</div>;
  return <AppLayout prefs={prefs} />;
}

function AuthGate() {
  const { user, loading } = useAuth();

  if (loading) return <div>Loading...</div>;

  if (!user) return <Login />;

  return (
    <UserPrefsProvider>
      <OnboardingProvider>
        <AppLayoutWrapper />
        {/* OnboardingGate renders fullscreen overlays (wizard, TheSession modal).
            To disable all onboarding: comment out the line below. */}
        <OnboardingGate />
      </OnboardingProvider>
    </UserPrefsProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}
